import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import {
  Plus, AlertCircle, Loader2, Trash2, ChevronRight, ChevronDown, Sparkles,
  Wand2, ShieldAlert, ExternalLink, X, Check,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function Budget() {
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [aiPanel, setAiPanel] = useState(null); // 'draft' | 'allocate' | 'check' | null
  const { toast } = useToast();

  const load = async () => {
    const { data } = await client.get('/budget/summary');
    setTotal(data.total);
    setCategories(data.categories);
    setOrphans(data.orphan_vendors || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const spent = useMemo(() => categories.reduce((a, c) => a + (c.spent || 0) + (c.vendor_paid || 0), 0), [categories]);
  const contracted = useMemo(() => categories.reduce((a, c) => a + Math.max(c.allocated || 0, c.vendor_total || 0), 0), [categories]);
  const allocated = useMemo(() => categories.reduce((a, c) => a + (c.allocated || 0), 0), [categories]);
  const left = total - spent;
  const percent = total ? Math.round((spent / total) * 100) : 0;

  const saveTotal = async (v) => { setTotal(v); try { await client.put('/budget', { total: v }); } catch {} };

  const updateCat = (idx, patch) => {
    const cat = categories[idx];
    setCategories((prev) => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
    client.put(`/budget/categories/${cat.id}`, patch).catch(() => {});
  };

  const addCat = async () => {
    const { data } = await client.post('/budget/categories', { name: 'New category', allocated: 500, color: '#D9B8A6' });
    setCategories((prev) => [...prev, { ...data, vendors: [], vendor_total: 0, vendor_paid: 0 }]);
    toast({ title: 'Category added' });
  };

  const removeCat = async (id) => {
    setCategories((prev) => prev.filter(c => c.id !== id));
    try { await client.delete(`/budget/categories/${id}`); } catch {}
  };

  const goToVendor = (vid) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'vendors');
    url.searchParams.set('open', vid);
    window.location.href = url.toString();
  };

  if (loading) return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading budget…</div>;

  return (
    <div className="animate-float-up">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mb-1 flex items-center gap-3"><AppIcon feature="budget" size="lg" reveal testId="section-icon-budget" />Smart budget</h1>
          <p className="text-[#6A6A6A]">Track every dollar. Click a category to see the vendors driving that number.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setAiPanel('draft')} data-testid="budget-ai-draft-btn" className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1"><Wand2 className="w-3 h-3" /> Draft a budget</button>
          <button onClick={() => setAiPanel('allocate')} data-testid="budget-ai-allocate-btn" className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Right-size</button>
          <button onClick={() => setAiPanel('check')} data-testid="budget-ai-check-btn" className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Check my spend</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="soft-card rounded-2xl p-6 lg:col-span-2">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-xs text-[#6A6A6A]">Total budget</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-serif text-3xl text-[#2A2A2A]">$</span>
                <input
                  type="number"
                  value={total}
                  onChange={(e) => saveTotal(Number(e.target.value) || 0)}
                  className="font-serif text-4xl text-[#2A2A2A] bg-transparent w-40 focus:outline-none border-b border-transparent focus:border-[#B8896C]"
                />
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#6A6A6A]">Remaining</p>
              <p className={`font-serif text-3xl ${left < 0 ? 'text-red-600' : 'text-[#7B2C3B]'}`}>${left.toLocaleString()}</p>
            </div>
          </div>
          <div className="relative h-3 w-full rounded-full bg-[#F4E4DA] overflow-hidden">
            <div className="h-full rounded-full bg-[#7B2C3B] transition-all" style={{ width: `${Math.min(100, percent)}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-[#6A6A6A]">
            <span>Spent ${spent.toLocaleString()} ({percent}%)</span>
            <span>Allocated ${allocated.toLocaleString()}</span>
          </div>
          {allocated > total && (
            <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-[#FDECEA] text-red-700 text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Your allocations exceed your total budget by ${(allocated - total).toLocaleString()}.
            </div>
          )}
          {contracted > total && (
            <div className="mt-2 flex items-start gap-2 p-3 rounded-xl bg-[#FFF7EC] text-[#7B4A1A] text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Contracted vendor totals already exceed the budget by ${(contracted - total).toLocaleString()}.
            </div>
          )}
        </div>

        <div className="soft-card rounded-2xl p-6 bg-[#F6EAE0]">
          <h3 className="font-serif text-xl text-[#2A2A2A] mb-3">Where it goes</h3>
          <div className="space-y-2">
            {categories.slice().sort((a, b) => (b.allocated || 0) - (a.allocated || 0)).map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                <span className="text-[#3A3A3A] flex-1 truncate">{c.name}</span>
                <span className="text-[#6A6A6A]">{total ? Math.round((c.allocated / total) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="soft-card rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-[#EFE3D8]">
          <div>
            <h3 className="font-serif text-xl text-[#2A2A2A]">Categories</h3>
            <p className="text-xs text-[#6A6A6A]">Click any row to drill into the vendors making up that number.</p>
          </div>
          <button onClick={addCat} data-testid="budget-add-cat" className="text-xs text-[#7B2C3B] inline-flex items-center gap-1.5 hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add category
          </button>
        </div>
        <div className="divide-y divide-[#EFE3D8]">
          {categories.map((c, i) => {
            const effectiveSpent = Math.max(c.spent || 0, c.vendor_paid || 0);
            const pct = c.allocated ? Math.min(100, Math.round((effectiveSpent / c.allocated) * 100)) : 0;
            const over = effectiveSpent > c.allocated;
            const isOpen = !!expanded[c.id];
            return (
              <div key={c.id} data-testid={`budget-row-${c.id}`}>
                <div className="p-5 grid grid-cols-12 items-center gap-3">
                  <button
                    onClick={() => setExpanded((prev) => ({ ...prev, [c.id]: !isOpen }))}
                    data-testid={`budget-row-toggle-${c.id}`}
                    className="col-span-12 md:col-span-3 flex items-center gap-2 text-left"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-[#B8896C]" /> : <ChevronRight className="w-4 h-4 text-[#B8896C]" />}
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                    <input value={c.name} onChange={(e) => updateCat(i, { name: e.target.value })} onClick={(e) => e.stopPropagation()} className="font-medium text-[#2A2A2A] bg-transparent focus:outline-none border-b border-transparent focus:border-[#B8896C]" />
                    {c.vendors && c.vendors.length > 0 && <span className="text-[10px] text-[#7B2C3B] bg-[#F4E4DA] px-1.5 py-0.5 rounded-full">{c.vendors.length}</span>}
                  </button>
                  <div className="col-span-6 md:col-span-3 flex items-center gap-2">
                    <span className="text-xs text-[#6A6A6A]">Allocated $</span>
                    <input type="number" value={c.allocated} onChange={(e) => updateCat(i, { allocated: Number(e.target.value) || 0 })} className="w-24 bg-transparent text-[#2A2A2A] focus:outline-none border-b border-transparent focus:border-[#B8896C]" />
                  </div>
                  <div className="col-span-6 md:col-span-3 flex items-center gap-2">
                    <span className="text-xs text-[#6A6A6A]">Spent $</span>
                    <input type="number" value={effectiveSpent} onChange={(e) => updateCat(i, { spent: Number(e.target.value) || 0 })} className="w-24 bg-transparent text-[#2A2A2A] focus:outline-none border-b border-transparent focus:border-[#B8896C]" />
                    {c.vendor_paid > 0 && (
                      <span className="text-[10px] text-[#9A8A7E]" title="Paid via linked vendors">· from vendors</span>
                    )}
                  </div>
                  <div className="col-span-10 md:col-span-2">
                    <div className="h-1.5 w-full rounded-full bg-[#F4E4DA] overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: over ? '#dc2626' : c.color }} />
                    </div>
                    <p className={`text-xs mt-1 ${over ? 'text-red-600' : 'text-[#6A6A6A]'}`}>{pct}% {over ? '— over' : 'used'}</p>
                  </div>
                  <div className="col-span-2 md:col-span-1 text-right">
                    <button onClick={() => removeCat(c.id)} className="text-[#B8896C] hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-5 pb-4 -mt-2 bg-[#FBF7F2]" data-testid={`budget-row-vendors-${c.id}`}>
                    {(c.vendors || []).length === 0 ? (
                      <p className="text-xs text-[#9A8A7E] italic py-3">
                        No vendors linked to this category yet. Set the "Budget category" field on any vendor to see it show up here.
                      </p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-widest text-[#9A8A7E] border-b border-[#EFE3D8]">
                            <th className="py-2">Vendor</th>
                            <th>Status</th>
                            <th className="text-right">Paid</th>
                            <th className="text-right">Contract</th>
                            <th className="text-right">Remaining</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.vendors.map((v) => (
                            <tr key={v.id} className="border-b border-[#EFE3D8]/60 last:border-0">
                              <td className="py-2 font-medium text-[#2A2A2A]">{v.name}</td>
                              <td className="text-[#6A6A6A]">{v.status || '—'}</td>
                              <td className="text-right text-[#4A7A4A]">${(v.paid || 0).toLocaleString()}</td>
                              <td className="text-right text-[#2A2A2A]">${(v.total || 0).toLocaleString()}</td>
                              <td className="text-right text-[#7B2C3B]">${((v.total || 0) - (v.paid || 0)).toLocaleString()}</td>
                              <td className="text-right">
                                <button onClick={() => goToVendor(v.id)} title="Open vendor" className="text-[#7B2C3B] hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /></button>
                              </td>
                            </tr>
                          ))}
                          <tr className="text-[#2A2A2A] font-medium">
                            <td className="py-2">Total</td>
                            <td></td>
                            <td className="text-right">${(c.vendor_paid || 0).toLocaleString()}</td>
                            <td className="text-right">${(c.vendor_total || 0).toLocaleString()}</td>
                            <td className="text-right">${((c.vendor_total || 0) - (c.vendor_paid || 0)).toLocaleString()}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {orphans.length > 0 && (
        <div className="mt-6 soft-card rounded-2xl p-5 bg-[#FFF7EC] border border-[#F5D9B7]" data-testid="budget-orphans">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-[#7B4A1A] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#7B4A1A]">{orphans.length} vendor{orphans.length === 1 ? '' : 's'} not linked to any budget category</p>
              <p className="text-xs text-[#7B4A1A]">Open the vendor and set the "Budget category" field to roll their totals into the right row.</p>
            </div>
          </div>
          <ul className="mt-2 space-y-1">
            {orphans.map((v) => (
              <li key={v.id} className="text-xs text-[#7B4A1A] flex items-center gap-2">
                <span>·</span>
                <button onClick={() => goToVendor(v.id)} className="hover:underline">{v.name}</button>
                <span className="text-[10px] text-[#9A8A7E]">${(v.total || 0).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {aiPanel && <BudgetAIPanel mode={aiPanel} total={total} categories={categories} onClose={() => setAiPanel(null)} onApplied={load} />}
    </div>
  );
}

function BudgetAIPanel({ mode, total, categories, onClose, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ guest_count: 100, region: 'US', style: 'classic', total: total || 30000, priorities: [] });
  const [result, setResult] = useState(null);
  const { toast } = useToast();
  useEffect(() => {
    if (mode === 'check') { run(); } // eslint-disable-next-line
  }, [mode]);

  const run = async () => {
    setBusy(true);
    try {
      if (mode === 'draft') {
        const { data } = await client.post('/budget/ai/draft', form);
        setResult(data);
      } else if (mode === 'allocate') {
        const { data } = await client.post('/budget/ai/allocate', { total: form.total, priorities: form.priorities });
        setResult(data);
      } else if (mode === 'check') {
        const { data } = await client.post('/budget/ai/check');
        setResult(data);
      }
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || 'AI request failed.' });
    } finally { setBusy(false); }
  };

  const applyDraft = async () => {
    if (!result?.categories) return;
    setBusy(true);
    try {
      await client.post('/budget/apply-draft', { total: result.total || form.total, categories: result.categories });
      toast({ title: 'Applied to your budget' });
      onApplied?.();
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
          <h2 className="font-serif text-lg text-[#2A2A2A] inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#7B2C3B]" />
            {mode === 'draft' ? 'Draft a budget with AI' : mode === 'allocate' ? 'Right-size your budget' : 'Check my spend'}
          </h2>
          <button onClick={onClose} className="text-[#9A8A7E] hover:text-[#7B2C3B]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {mode === 'draft' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Guest count</span><input type="number" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: Number(e.target.value) || 0 })} className="input" /></label>
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Total budget ($)</span><input type="number" value={form.total} onChange={(e) => setForm({ ...form, total: Number(e.target.value) || 0 })} className="input" /></label>
              <label className="block col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Region</span><input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder='e.g. "Los Angeles" or "Rural Midwest"' className="input" /></label>
              <label className="block col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Style</span><input value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} placeholder="boho / classic / modern minimalist / …" className="input" /></label>
            </div>
          )}
          {mode === 'allocate' && (
            <div className="space-y-3">
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Total budget ($)</span><input type="number" value={form.total} onChange={(e) => setForm({ ...form, total: Number(e.target.value) || 0 })} className="input" /></label>
              <div>
                <span className="text-[10px] uppercase tracking-widest text-[#9A8A7E] block mb-1.5">Priorities (spend more here)</span>
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((c) => {
                    const on = form.priorities.includes(c.name);
                    return (
                      <button key={c.id} type="button"
                              onClick={() => setForm((f) => ({ ...f, priorities: on ? f.priorities.filter(p => p !== c.name) : [...f.priorities, c.name] }))}
                              className={`text-xs px-3 py-1 rounded-full border ${on ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8] text-[#3A3A3A]'}`}>
                        {on && <Check className="inline w-3 h-3 mr-1" />}{c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {mode === 'check' && (
            <p className="text-sm text-[#6A6A6A]">Claude reads your current allocations + linked vendor totals and flags pinch points.</p>
          )}

          {result?.error && <p className="text-sm text-red-600">{result.error}</p>}

          {result?.categories && (
            <div className="mt-2 border border-[#EFE3D8] rounded-xl overflow-hidden">
              <div className="p-3 bg-[#FBF7F2] border-b border-[#EFE3D8] text-xs text-[#7B2C3B] font-medium">Proposed allocation</div>
              <ul className="divide-y divide-[#EFE3D8]">
                {result.categories.map((c, i) => (
                  <li key={i} className="p-2.5 flex items-center justify-between text-xs">
                    <span className="text-[#2A2A2A]">{c.name}</span>
                    <span className="text-[#7B2C3B] font-medium">${Math.round(c.allocated).toLocaleString()}</span>
                  </li>
                ))}
                <li className="p-2.5 flex items-center justify-between text-xs bg-[#FBF7F2] font-medium">
                  <span>Total</span>
                  <span>${Math.round(result.categories.reduce((a, c) => a + Number(c.allocated || 0), 0)).toLocaleString()}</span>
                </li>
              </ul>
            </div>
          )}

          {result?.warnings && (
            <ul className="space-y-2 mt-2">
              {result.warnings.map((w, i) => (
                <li key={i} className={`p-3 rounded-xl text-xs ${w.severity === 'critical' ? 'bg-[#FDECEA] text-red-700' : w.severity === 'warn' ? 'bg-[#FFF7EC] text-[#7B4A1A]' : 'bg-[#EFF6EF] text-[#3F6B3F]'}`}>
                  <span className="uppercase tracking-widest text-[10px] mr-1.5 opacity-70">{w.severity}</span>{w.message}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-between gap-2 bg-white">
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8]">Close</button>
          <div className="flex items-center gap-2">
            {(mode !== 'check' || !result) && (
              <button onClick={run} disabled={busy} data-testid="budget-ai-run" className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {busy ? 'Thinking…' : (result ? 'Try again' : 'Generate')}
              </button>
            )}
            {result?.categories && (
              <button onClick={applyDraft} disabled={busy} data-testid="budget-ai-apply" className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60">
                <Check className="w-3.5 h-3.5" /> Apply to my budget
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`.input{width:100%;padding:0.5rem 0.75rem;border-radius:0.75rem;border:1px solid #EFE3D8;font-size:0.875rem;background:#FBF7F2;margin-top:0.25rem}.input:focus{outline:none;border-color:#7B2C3B}`}</style>
    </div>
  );
}
